import type { ToolState } from '@/lib/assistant/tool-status';
import { readTrimmedString } from '@/lib/prop-utils';
import type { Message } from '@/types/assistant';

export type SiteAnalysisToolSummary = {
  label: string;
  subtitle?: string;
};

const ANALYZE_EXTERNAL_WEB_SITE_SUMMARY_BY_STATE: Record<ToolState, string> = {
  'input-streaming': 'Getting ready to scan pages...',
  'input-available': 'Scanning pages...',
  'output-available': 'Website review finished',
  failed: "Couldn't review that website",
};

type SiteScopeWebAnalysisProgressInput = {
  state: ToolState;
  input?: Record<string, unknown>;
  message?: Message;
};

/** Site analysis progress: title line, optional full URL on the second line. */
export function parseSiteAnalysisBackgroundTaskOutput(output: string): {
  message: string;
  pageUrl?: string;
} {
  const trimmed = output.trim();
  const newlineIndex = trimmed.indexOf('\n');
  if (newlineIndex === -1) {
    return { message: trimmed };
  }

  const message = trimmed.slice(0, newlineIndex).trim();
  const pageUrl = trimmed.slice(newlineIndex + 1).trim();
  if (pageUrl.startsWith('http://') || pageUrl.startsWith('https://')) {
    return { message, pageUrl };
  }

  return { message: trimmed };
}

function getAnalyzeExternalWebSiteSummary(state: ToolState): SiteAnalysisToolSummary {
  return { label: ANALYZE_EXTERNAL_WEB_SITE_SUMMARY_BY_STATE[state] };
}

export function getSiteScopeWebAnalysisProgressSummary({
  state,
  input = {},
  message,
}: SiteScopeWebAnalysisProgressInput): SiteAnalysisToolSummary {
  const progressMessage = message?.backgroundTaskProgressMessage?.trim();
  if (progressMessage && message?.backgroundTaskStatus === 'running') {
    const { message: label, pageUrl } = parseSiteAnalysisBackgroundTaskOutput(progressMessage);
    const targetUrl = readTrimmedString(input.url);
    const subtitle = pageUrl ?? targetUrl;
    return subtitle ? { label, subtitle } : { label };
  }

  const summary = getAnalyzeExternalWebSiteSummary(state);
  const targetUrl = readTrimmedString(input.url);
  return targetUrl ? { ...summary, subtitle: targetUrl } : summary;
}
